import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Upload, FileText, LayoutGrid, List, Sparkles } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import TaxBadge from "@/components/app/TaxBadge";

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [folders, setFolders] = useState([]);
  const [q, setQ] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [folder, setFolder] = useState(searchParams.get("folder") || "all");
  const [tax, setTax] = useState(searchParams.get("tax") || "all");
  const [view, setView] = useState("grid");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    if (q && q.trim().length >= 2 && folder === "all" && tax === "all") {
      // Rich search with snippets
      api.get("/search", { params: { q } })
        .then(r => setDocs(r.data.results.map(x => ({
          id: x.id,
          smart_filename: x.smart_filename,
          category: x.category,
          folder_name: x.folder_name,
          vendor: x.vendor,
          document_date: x.document_date,
          total_amount: x.total_amount,
          tax_relevance: x.tax_relevance,
          mime_type: x.mime_type,
          _snippet: x.snippet,
          _matched_field: x.matched_field,
        }))))
        .finally(() => setLoading(false));
      return;
    }
    const params = {};
    if (q) params.q = q;
    if (folder !== "all") params.folder_id = folder;
    if (tax !== "all") params.tax = tax;
    api.get("/documents", { params }).then(r => setDocs(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { api.get("/folders").then(r => setFolders(r.data)); }, []);
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (folder !== "all") next.set("folder", folder); else next.delete("folder");
    if (tax !== "all") next.set("tax", tax); else next.delete("tax");
    setSearchParams(next, { replace: true });
    load();
    // eslint-disable-next-line
  }, [folder, tax]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-8">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="overline text-[#0A0A0A] mb-3">— ARCHIVIO</div>
          <h1 className="font-display font-bold text-4xl tracking-tighter">Documenti</h1>
        </div>
        <Link to="/app/upload">
          <Button data-testid="docs-upload-btn" className="rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white h-10">
            <Upload className="w-4 h-4 mr-2" /> Nuovo
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white border border-black/10 rounded-xl p-4 mb-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]" />
          <Input
            data-testid="docs-search-input"
            placeholder="Cerca per nome, esercente, contenuto…"
            value={q}
            onChange={e => setQ(e.target.value)}
            className="pl-9 rounded-xl bg-white h-10 focus-visible:ring-[#0A0A0A] border-black/15"
          />
        </div>
        <Select value={folder} onValueChange={setFolder}>
          <SelectTrigger data-testid="docs-folder-filter" className="w-48 rounded-xl h-10 border-black/15">
            <SelectValue placeholder="Tutte le cartelle" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le cartelle</SelectItem>
            {folders.map(f => (
              <SelectItem key={f.id} value={f.id}>{f.name} ({f.document_count})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={tax} onValueChange={setTax}>
          <SelectTrigger data-testid="docs-tax-filter" className="w-44 rounded-xl h-10 border-black/15">
            <SelectValue placeholder="Tutti" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i documenti</SelectItem>
            <SelectItem value="any">
              <span className="flex items-center gap-2"><Sparkles className="w-3 h-3 text-[#009966]" /> Rilevanti fiscali</span>
            </SelectItem>
            <SelectItem value="isee">Solo ISEE</SelectItem>
            <SelectItem value="730_cu">Solo 730 / CU</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex border border-black/15 rounded-xl h-10 overflow-hidden">
          <button
            data-testid="view-grid-btn"
            onClick={() => setView("grid")}
            className={`px-3 flex items-center transition-colors ${view === "grid" ? "bg-[#0A0A0A] text-white" : "hover:bg-black/5"}`}
          ><LayoutGrid className="w-4 h-4" /></button>
          <button
            data-testid="view-list-btn"
            onClick={() => setView("list")}
            className={`px-3 flex items-center border-l border-black/15 transition-colors ${view === "list" ? "bg-[#0A0A0A] text-white" : "hover:bg-black/5"}`}
          ><List className="w-4 h-4" /></button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[#8A8A8A]">Caricamento…</div>
      ) : docs.length === 0 ? (
        <div className="bg-white border border-dashed border-black/15 rounded-xl p-16 text-center">
          <FileText className="w-12 h-12 text-black/20 mx-auto mb-4" />
          <h3 className="font-display font-bold text-xl mb-2 tracking-tight">Nessun documento</h3>
          <p className="text-sm text-[#5C5C5C] mb-6">Carica il primo documento per iniziare.</p>
          <Link to="/app/upload">
            <Button className="rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white">
              <Upload className="w-4 h-4 mr-2" /> Carica
            </Button>
          </Link>
        </div>
      ) : view === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {docs.map((d, i) => (
            <motion.div
              key={d.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
            >
              <Link
                to={`/app/documenti/${d.id}`}
                data-testid={`doc-card-${d.id}`}
                className="block bg-white border border-black/10 rounded-xl overflow-hidden hover:border-black/25 transition-colors group"
              >
                <div className="aspect-[4/5] bg-[#FAFAFA] border-b border-black/5 relative overflow-hidden">
                  {d.mime_type?.startsWith("image/") ? (
                    <img src={fileUrl(d.id)} alt={d.smart_filename} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center flex-col gap-2">
                      <FileText className="w-16 h-16 text-black/15" />
                      <span className="font-mono-plex text-[10px] text-[#8A8A8A]">PDF</span>
                    </div>
                  )}
                  <div className="absolute top-2 left-2 overline bg-white/90 px-2 py-1 border border-black/10 rounded-xl">
                    {d.category}
                  </div>
                  {d.tax_relevance && d.tax_relevance !== "none" && (
                    <div className="absolute top-2 right-2">
                      <TaxBadge relevance={d.tax_relevance} />
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="text-sm font-medium truncate mb-1" title={d.smart_filename}>{d.smart_filename}</div>
                  <div className="flex justify-between text-xs text-[#8A8A8A]">
                    <span className="truncate">{d.vendor || "—"}</span>
                    {d.total_amount != null && <span className="font-mono-plex">€ {Number(d.total_amount).toFixed(2)}</span>}
                  </div>
                  {d._snippet && d._matched_field === "extracted_text" && (
                    <div className="mt-2 pt-2 border-t border-black/5 text-[11px] text-[#5C5C5C] leading-snug line-clamp-2 italic" data-testid={`doc-snippet-${d.id}`}>
                      "{d._snippet}"
                    </div>
                  )}
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-black/10 rounded-xl overflow-hidden">
          <div className="grid grid-cols-12 px-4 py-3 border-b border-black/10 overline text-[#8A8A8A] text-[10px]">
            <div className="col-span-5">NOME</div>
            <div className="col-span-2">CATEGORIA</div>
            <div className="col-span-2">ESERCENTE</div>
            <div className="col-span-1">DATA</div>
            <div className="col-span-2 text-right">IMPORTO</div>
          </div>
          {docs.map(d => (
            <Link
              key={d.id}
              to={`/app/documenti/${d.id}`}
              data-testid={`doc-row-${d.id}`}
              className="grid grid-cols-12 px-4 py-3 border-b border-black/5 last:border-b-0 hover:bg-black/5 transition-colors text-sm items-center"
            >
              <div className="col-span-5 flex items-center gap-3 min-w-0">
                <FileText className="w-4 h-4 text-[#0A0A0A] shrink-0" />
                <span className="truncate">{d.smart_filename}</span>
                <TaxBadge relevance={d.tax_relevance} withIcon={false} />
              </div>
              <div className="col-span-2 text-xs text-[#5C5C5C]">{d.category}</div>
              <div className="col-span-2 text-xs text-[#5C5C5C] truncate">{d.vendor || "—"}</div>
              <div className="col-span-1 text-xs text-[#5C5C5C] font-mono-plex">{d.document_date || "—"}</div>
              <div className="col-span-2 text-right font-mono-plex text-sm">
                {d.total_amount != null ? `€ ${Number(d.total_amount).toFixed(2)}` : "—"}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
